<!-- src/components/NavHeader.vue -->
<template>
    <header class="site-header" :class="{ 'is-scrolled': isScrolled }">
        <nav class="nav-shell" aria-label="Primary">
            <!-- Brand -->
            <RouterLink class="brand" to="/" @click="closeMenu">
                <img class="brand-logo" :src="logoSrc" alt="M.C Bridge of Hope logo" />

                <span class="brand-text">
                    <span class="brand-name">M.C</span>
                    <span class="brand-sub">Bridge of Hope</span>
                </span>
            </RouterLink>

            <!-- Desktop nav -->
            <div class="nav-links" role="navigation">
                <RouterLink v-for="item in mainLinks" :key="item.key" class="nav-link"
                    :class="{ 'is-active': isActive(item.href) }" :to="item.href">
                    {{ item.label }}
                </RouterLink>

                <RouterLink class="donate-link" :class="{ 'is-active': isActive(donateHref) }" :to="donateHref">
                    Donations
                </RouterLink>
            </div>

            <!-- Actions -->
            <div class="nav-actions">
                <!-- Desktop social icons -->
                <div class="social-mini" aria-label="Social links">
                    <a v-for="s in socialLinks" :key="s.key" class="social-icon" :href="s.href" target="_blank"
                        rel="noopener noreferrer" :aria-label="s.label" :title="s.label">
                        <span class="svg-icon" aria-hidden="true" v-html="s.svg"></span>
                    </a>
                </div>

                <!-- Mobile menu button -->
                <button class="menu-btn" type="button" :aria-expanded="menuOpen ? 'true' : 'false'"
                    aria-controls="mobile-menu" @click="toggleMenu">
                    <span class="menu-icon" aria-hidden="true">
                        <span class="bar bar-top" :class="{ 'is-open': menuOpen }"></span>

                        <span class="bar bar-mid" :class="{ 'is-open': menuOpen }"></span>

                        <span class="bar bar-bot" :class="{ 'is-open': menuOpen }"></span>
                    </span>

                    <span class="sr-only">
                        {{ menuOpen ? "Close menu" : "Open menu" }}
                    </span>
                </button>
            </div>
        </nav>

        <!-- Mobile overlay -->
        <transition name="fade">
            <div v-if="menuOpen" class="backdrop" @click="closeMenu" aria-hidden="true"></div>
        </transition>

        <!-- Mobile drawer -->
        <transition name="slide">
            <div v-if="menuOpen" id="mobile-menu" class="mobile-panel" role="dialog" aria-modal="true"
                aria-label="Mobile navigation">
                <div class="mobile-head">
                    <div class="mobile-title">Menu</div>

                    <button class="close-btn" type="button" @click="closeMenu">
                        <span aria-hidden="true">✕</span>
                        <span class="sr-only">Close</span>
                    </button>
                </div>

                <div class="mobile-links">
                    <RouterLink v-for="item in mainLinks" :key="item.key" class="mobile-link"
                        :class="{ 'is-active': isActive(item.href) }" :to="item.href" @click="closeMenu">
                        {{ item.label }}
                    </RouterLink>

                    <RouterLink class="mobile-donate" :class="{ 'is-active': isActive(donateHref) }" :to="donateHref"
                        @click="closeMenu">
                        Donations
                    </RouterLink>

                    <!-- Mobile social links -->
                    <div class="mobile-social">
                        <div class="mobile-social-title">
                            Connect with us
                        </div>

                        <div class="mobile-social-row">
                            <a v-for="s in socialLinks" :key="s.key" class="mobile-social-chip" :href="s.href"
                                target="_blank" rel="noopener noreferrer" :aria-label="s.label">
                                <span class="chip-svg" aria-hidden="true" v-html="s.svg"></span>

                                <span class="chip-text">
                                    {{ s.label }}
                                </span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </header>
</template>

<script setup>
import {
    computed,
    onBeforeUnmount,
    onMounted,
    ref,
} from "vue";

import { useRoute } from "vue-router";

import logoSrc from "@/assets/logo7.svg";

const route = useRoute();

const menuOpen = ref(false);
const isScrolled = ref(false);

const activePath = computed(() => route.path);

function isActive(href) {
    const cur = activePath.value || "/";

    if (href === "/") {
        return cur === "/";
    }

    return cur === href || cur.startsWith(href + "/");
}

function toggleMenu() {
    menuOpen.value = !menuOpen.value;

    lockBodyScroll(menuOpen.value);
}

function closeMenu() {
    menuOpen.value = false;

    lockBodyScroll(false);
}

function lockBodyScroll(lock) {
    document.body.style.overflow = lock ? "hidden" : "";
}

function onScroll() {
    isScrolled.value = window.scrollY > 8;
}


/* -------------------------------------------------------
   SOCIAL ICONS
------------------------------------------------------- */

const ICONS = {
    instagram: `
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M7.5 2.75h9A4.75 4.75 0 0 1 21.25 7.5v9A4.75 4.75 0 0 1 16.5 21.25h-9A4.75 4.75 0 0 1 2.75 16.5v-9A4.75 4.75 0 0 1 7.5 2.75Z"
                stroke="currentColor"
                stroke-width="1.7"
            />

            <path
                d="M12 16.25A4.25 4.25 0 1 0 12 7.75a4.25 4.25 0 0 0 0 8.5Z"
                stroke="currentColor"
                stroke-width="1.7"
            />

            <path
                d="M17.2 6.9h.01"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
            />
        </svg>
    `,

    facebook: `
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M13.5 21v-7h2.4l.4-3h-2.8V9.1c0-.9.3-1.6 1.7-1.6H16.4V4.8c-.6-.1-1.6-.2-2.7-.2-2.6 0-4.2 1.6-4.2 4.5V11H7.2v3h2.3v7h4Z"
            />
        </svg>
    `,

    linkedin: `
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M6.5 8.25H3.25V21H6.5V8.25ZM4.88 3A1.88 1.88 0 1 0 4.88 6.75 1.88 1.88 0 0 0 4.88 3ZM21 13.7C21 9.85 18.94 8.06 16.2 8.06c-2.21 0-3.2 1.22-3.76 2.07V8.25H9.2V21h3.24v-6.32c0-1.67.32-3.29 2.39-3.29 2.04 0 2.06 1.91 2.06 3.4V21H21v-7.3Z"
            />
        </svg>
    `,

    tiktok: `
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M14.5 3h3.1c.25 1.45 1.06 2.67 2.4 3.45V9.5a8.14 8.14 0 0 1-2.72-.72v6.08A6.14 6.14 0 1 1 12 8.78v3.12a3.06 3.06 0 1 0 2.5 3V3Z"
            />
        </svg>
    `,
};


/* -------------------------------------------------------
   NAVIGATION
------------------------------------------------------- */

const mainLinks = [
    {
        key: "home",
        label: "Home",
        href: "/",
    },

    {
        key: "programs",
        label: "Programs",
        href: "/programs",
    },

    {
        key: "about",
        label: "About Us",
        href: "/about",
    },

    {
        key: "contact",
        label: "Contact",
        href: "/contact",
    },

    {
        key: "social",
        label: "Social",
        href: "/social",
    },
];

const donateHref = "/donate";


/* -------------------------------------------------------
   SOCIAL LINKS
------------------------------------------------------- */

const socialLinks = [
    {
        key: "instagram",
        label: "Instagram",
        href: "https://www.instagram.com/bridgeofhope.mc/",
        svg: ICONS.instagram,
    },

    {
        key: "facebook",
        label: "Facebook",
        href: "https://www.facebook.com/profile.php?id=61594445814032",
        svg: ICONS.facebook,
    },

    {
        key: "linkedin",
        label: "LinkedIn",
        href: "https://www.linkedin.com/in/mc-bridge-of-hope-44aa23434/",
        svg: ICONS.linkedin,
    },

    {
        key: "tiktok",
        label: "TikTok",
        href: "https://www.tiktok.com/@marychan.org?_r=1&_t=ZS-99dXxN98DoL",
        svg: ICONS.tiktok,
    },
];


/* -------------------------------------------------------
   LIFECYCLE
------------------------------------------------------- */

onMounted(() => {
    window.addEventListener("scroll", onScroll, {
        passive: true,
    });

    onScroll();
});

onBeforeUnmount(() => {
    window.removeEventListener("scroll", onScroll);

    lockBodyScroll(false);
});
</script>

<style scoped lang="scss">
/* -------------------------------------------------------
   HEADER
------------------------------------------------------- */

.site-header {
    position: sticky;

    top: 0;

    z-index: 60;

    /* Original background colour retained */
    background: rgba(245, 247, 250, 0.75);

    backdrop-filter: blur(10px);

    border-bottom: 1px solid rgba(7, 56, 77, 0.08);

    transition:
        background 180ms ease,
        box-shadow 180ms ease;
}

.site-header.is-scrolled {
    /* Original background colour retained */
    background: rgba(245, 247, 250, 0.92);

    box-shadow: 0 10px 30px rgba(4, 43, 60, 0.08);
}


/* -------------------------------------------------------
   NAV
------------------------------------------------------- */

.nav-shell {
    max-width: 1200px;

    margin: 0 auto;

    display: grid;

    grid-template-columns: 1fr auto;

    align-items: center;

    gap: 14px;

    @media (max-width: 768px) {
        padding: 0 4%;
    }
}


/* -------------------------------------------------------
   BRAND
------------------------------------------------------- */

.brand {
    display: flex;

    align-items: center;

    gap: 12px;

    text-decoration: none;

    min-width: 0;

    height: 80px;
}

.brand-logo {
    width: 210px;

    border-radius: 5px;

    position: relative;

    top: 1px;

    margin-right: 0;

    @media (max-width: 768px) {
        width: 160px;

        margin-right: 0;
    }

    @media (max-width: 320px) {
        width: 130px;

        left: -10px;
    }
}

.brand-text {
    display: grid;

    line-height: 1.05;

    position: relative;

    left: -40px;

    top: 10px;

    @media (max-width: 768px) {
        left: -60px;
    }
}

.brand-name {
    font-weight: 800;

    letter-spacing: 0.2px;

    font-size: 18px;

    color: #07384d;

    position: relative;

    @media (max-width: 768px) {
        width: 150px;
    }
}

.brand-sub {
    font-size: 12px;

    margin-top: 3px;
    font-weight: 900;
    letter-spacing: 0.3px;

    color: #c5a36d;
}


/* -------------------------------------------------------
   DESKTOP NAV
------------------------------------------------------- */

.nav-links {
    display: none;

    align-items: center;

    gap: 10px;
}

.nav-link {
    text-decoration: none;

    padding: 10px;

    border-radius: 999px;

    font-weight: 650;

    font-size: 14px;

    color: rgba(4, 43, 60, 0.82);

    transition:
        transform 160ms ease,
        background 160ms ease,
        color 160ms ease;

    white-space: nowrap;

    &:hover {
        background: rgba(197, 163, 109, 0.12);

        transform: translateY(-1px);
    }

    &.is-active {
        background: rgba(197, 163, 109, 0.18);

        color: #07384d;
    }
}


/* -------------------------------------------------------
   DONATE
------------------------------------------------------- */

.donate-link {
    margin-left: 6px;

    text-decoration: none;

    padding: 10px 14px;

    border-radius: 999px;

    font-weight: 800;

    font-size: 14px;

    background: #07384d;

    color: #fff;

    box-shadow: 0 10px 20px rgba(7, 56, 77, 0.22);

    transition:
        transform 160ms ease,
        filter 160ms ease,
        background 160ms ease;

    white-space: nowrap;

    &:hover {
        transform: translateY(-1px);

        background: #042b3c;
    }

    &.is-active {
        background: #042b3c;
    }
}


/* -------------------------------------------------------
   ACTIONS
------------------------------------------------------- */

.nav-actions {
    display: flex;

    align-items: center;

    gap: 10px;
}


/* -------------------------------------------------------
   SOCIAL ICONS
------------------------------------------------------- */

.social-mini {
    display: none;

    align-items: center;

    gap: 8px;
}

.social-icon {
    width: 34px;

    height: 34px;

    border-radius: 12px;

    display: grid;

    place-items: center;

    text-decoration: none;

    border: 1px solid rgba(7, 56, 77, 0.08);

    background: rgba(255, 255, 255, 0.7);

    transition:
        transform 160ms ease,
        background 160ms ease,
        border-color 160ms ease;

    color: #07384d;

    &:hover {
        transform: translateY(-2px);

        background: rgba(197, 163, 109, 0.12);

        border-color: rgba(197, 163, 109, 0.25);
    }
}

.svg-icon {
    display: grid;

    place-items: center;
}

.svg-icon :deep(svg) {
    display: block;
}


/* -------------------------------------------------------
   MOBILE MENU BUTTON
------------------------------------------------------- */

.menu-btn {
    width: 44px;

    height: 44px;

    border-radius: 14px;

    border: 1px solid rgba(7, 56, 77, 0.10);

    background: rgba(255, 255, 255, 0.75);

    display: grid;

    place-items: center;

    cursor: pointer;

    transition:
        transform 160ms ease,
        background 160ms ease;

    &:hover {
        transform: translateY(-1px);

        background: rgba(197, 163, 109, 0.12);
    }
}

.menu-icon {
    width: 18px;

    height: 14px;

    position: relative;
}

.bar {
    position: absolute;

    left: 0;

    right: 0;

    height: 2px;

    background: #07384d;

    border-radius: 999px;

    transition:
        transform 180ms ease,
        top 180ms ease,
        opacity 180ms ease;

    &.bar-top {
        top: 0;
    }

    &.bar-mid {
        top: 6px;
    }

    &.bar-bot {
        top: 12px;
    }

    &.bar-top.is-open {
        top: 6px;

        transform: rotate(45deg);
    }

    &.bar-mid.is-open {
        opacity: 0;
    }

    &.bar-bot.is-open {
        top: 6px;

        transform: rotate(-45deg);
    }
}


/* -------------------------------------------------------
   BACKDROP
------------------------------------------------------- */

.backdrop {
    position: fixed;

    inset: 0;

    background: rgba(4, 43, 60, 0.35);

    z-index: 59;
}


/* -------------------------------------------------------
   MOBILE PANEL
------------------------------------------------------- */

.mobile-panel {
    position: fixed;

    right: 12px;

    top: 12px;

    width: min(420px, calc(100vw - 24px));

    border-radius: 22px;

    background: rgba(255, 255, 255, 0.92);

    backdrop-filter: blur(12px);

    border: 1px solid rgba(7, 56, 77, 0.10);

    box-shadow: 0 30px 80px rgba(4, 43, 60, 0.22);

    z-index: 60;

    overflow: hidden;
}

.mobile-head {
    padding: 14px 14px 10px;

    display: flex;

    align-items: center;

    justify-content: space-between;
}

.mobile-title {
    font-weight: 900;

    color: #07384d;

    letter-spacing: 0.2px;
}

.close-btn {
    width: 40px;

    height: 40px;

    border-radius: 14px;

    border: 1px solid rgba(7, 56, 77, 0.10);

    background: rgba(255, 255, 255, 0.9);

    color: #07384d;

    cursor: pointer;
}

.mobile-links {
    padding: 8px 14px 16px;

    display: grid;

    gap: 8px;
}

.mobile-link {
    text-decoration: none;

    padding: 12px;

    border-radius: 16px;

    background: rgba(7, 56, 77, 0.06);

    border: 1px solid rgba(7, 56, 77, 0.06);

    font-weight: 750;

    color: rgba(4, 43, 60, 0.85);

    &.is-active {
        background: rgba(197, 163, 109, 0.18);

        color: #07384d;

        border-color: rgba(197, 163, 109, 0.26);
    }
}

.mobile-donate {
    margin-top: 6px;

    text-decoration: none;

    padding: 12px;

    border-radius: 16px;

    font-weight: 900;

    background: #07384d;

    color: #fff;

    text-align: center;

    box-shadow: 0 12px 24px rgba(7, 56, 77, 0.22);

    &.is-active {
        background: #042b3c;
    }
}


/* -------------------------------------------------------
   MOBILE SOCIAL
------------------------------------------------------- */

.mobile-social {
    margin-top: 8px;

    padding-top: 12px;

    border-top: 1px solid rgba(7, 56, 77, 0.08);
}

.mobile-social-title {
    font-size: 12px;

    font-weight: 850;

    color: rgba(4, 43, 60, 0.65);

    margin-bottom: 10px;
}

.mobile-social-row {
    display: flex;

    flex-wrap: wrap;

    gap: 8px;
}

.mobile-social-chip {
    display: inline-flex;

    align-items: center;

    gap: 9px;

    text-decoration: none;

    padding: 10px 12px;

    border-radius: 999px;

    background: rgba(255, 255, 255, 0.85);

    border: 1px solid rgba(7, 56, 77, 0.08);

    color: rgba(4, 43, 60, 0.85);

    font-weight: 750;

    transition:
        transform 160ms ease,
        background 160ms ease;

    &:hover {
        background: rgba(197, 163, 109, 0.12);

        transform: translateY(-1px);
    }
}

.chip-svg {
    color: #07384d;

    display: grid;

    place-items: center;
}

.chip-svg :deep(svg) {
    display: block;
}


/* -------------------------------------------------------
   ACCESSIBILITY
------------------------------------------------------- */

.sr-only {
    position: absolute;

    width: 1px;

    height: 1px;

    padding: 0;

    margin: -1px;

    overflow: hidden;

    clip: rect(0, 0, 0, 0);

    white-space: nowrap;

    border: 0;
}


/* -------------------------------------------------------
   TRANSITIONS
------------------------------------------------------- */

.fade-enter-active,
.fade-leave-active {
    transition: opacity 140ms ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
    transition:
        transform 160ms ease,
        opacity 160ms ease;
}

.slide-enter-from {
    transform: translateY(-8px);

    opacity: 0;
}

.slide-leave-to {
    transform: translateY(-8px);

    opacity: 0;
}


/* -------------------------------------------------------
   DESKTOP
------------------------------------------------------- */

@media (min-width: 960px) {
    .nav-shell {
        grid-template-columns: auto 1fr auto;
    }

    .nav-links {
        display: flex;

        justify-content: center;
    }

    .social-mini {
        display: flex;
    }

    .menu-btn {
        display: none;
    }

    .mobile-panel,
    .backdrop {
        display: none !important;
    }
}
</style>